/**
 * Name, path and CMMD icon for every project key a sidebar thread can point at.
 *
 * Extracted from `activeThreadsSection.logic` (FOR-36687). A project is filed
 * under every key it can arrive by: its raw `environmentId:projectId` and, for
 * a cloud runtime, the runtime-scoped alias shared by the legacy
 * `cmmd-runtime:` locator and CMMD's durable `env_…` key. See
 * `sidebarThreadRuntimeIdentity` for why one runtime has two keys.
 */

import type { ProjectIdentity } from "./activeThreadsSection.logic";
import { resolveCanonicalProjectIdentity, type ProjectGroup } from "./projectGroupedSidebar.logic";
import {
  runtimeScopedSidebarKey,
  type SavedEnvironmentRuntimeIndex,
} from "./sidebarThreadRuntimeIdentity";

export interface ProjectDisplayIdentity extends ProjectIdentity {
  readonly name?: string | null | undefined;
  readonly cwd?: string | null | undefined;
  readonly worktreeMembers?:
    | readonly {
        readonly projectRef: { readonly environmentId: string; readonly projectId: string };
        readonly cwd?: string | null | undefined;
      }[]
    | undefined;
}

export interface ProjectDisplayEntry {
  readonly name: string | null;
  /** Feeds `ProjectFavicon`, which reads the repo identity out of the path. */
  readonly cwd: string | null;
  /**
   * The CMMD-owned icon, when the project's group carries one. Present exactly
   * when the picker would draw a canonical icon for the same project, so a
   * thread card and the picker cannot disagree about what a project looks like.
   */
  readonly canonicalIdentity?: { readonly iconUrl: string | null; readonly icon: string | null };
}

/**
 * Name and path for every project key a thread can point at.
 *
 * This has to accept the SAME project list as {@link buildKnownProjectKeySet},
 * not just the live environment store. A thread is only shown as loose when its
 * key is unknown to that set — which already spans CMMD's durable replica — so
 * indexing display data from a narrower list produces rows that are placed
 * correctly and then labelled "Unknown project". That is exactly what happens
 * when the owning runtime is not connected: the live store holds no projects for
 * it, while the replica holds both the threads and their project names.
 *
 * Keys are registered from three places, because one sidebar project can stand
 * for several physical projects:
 *  - its own `${environmentId}:${id}`,
 *  - each `worktreeMembers[].projectRef` (which carries its own `cwd`),
 *  - each `memberProjectRefs[]` entry.
 *
 * Earlier projects win per field, so callers put the authoritative (live) list
 * first and let the replica fill only what the live store is missing.
 */
export function buildProjectDisplayByKey(
  projects: readonly ProjectDisplayIdentity[],
  groups: readonly ProjectGroup[] = [],
  environments: SavedEnvironmentRuntimeIndex = {},
): ReadonlyMap<string, ProjectDisplayEntry> {
  const displayByKey = new Map<string, ProjectDisplayEntry>();
  const registerKey = (key: string, name: string | null, cwd: string | null) => {
    const existing = displayByKey.get(key);
    if (!existing) {
      displayByKey.set(key, { name, cwd });
      return;
    }
    if (existing.name !== null && existing.cwd !== null) return;
    displayByKey.set(key, {
      name: existing.name ?? name,
      cwd: existing.cwd ?? cwd,
    });
  };
  const register = (
    environmentId: string,
    projectId: string,
    name: string | null,
    cwd: string | null,
  ) => {
    for (const key of projectDisplayKeys(environmentId, projectId, environments)) {
      registerKey(key, name, cwd);
    }
  };

  for (const project of projects) {
    const name = project.name?.trim() ? project.name : null;
    const cwd = project.cwd?.trim() ? project.cwd : null;
    register(project.environmentId, project.id, name, cwd);
    for (const member of project.worktreeMembers ?? []) {
      register(
        member.projectRef.environmentId,
        member.projectRef.projectId,
        name,
        member.cwd?.trim() ? member.cwd : cwd,
      );
    }
    for (const member of project.memberProjectRefs ?? []) {
      register(member.environmentId, member.projectId, name, cwd);
    }
  }

  overlayCanonicalProjectIcons(displayByKey, groups, environments);
  return displayByKey;
}

/**
 * Every key a project is filed under: its raw `environmentId:projectId`, plus
 * the runtime-scoped alias when the environment names a cloud runtime. The
 * alias is what lets a row that arrived under the durable `env_…` key find the
 * project the catalog registered under the `cmmd-runtime:` locator, and vice
 * versa (FOR-36687).
 */
function projectDisplayKeys(
  environmentId: string,
  projectId: string,
  environments: SavedEnvironmentRuntimeIndex,
): readonly string[] {
  const raw = `${environmentId}:${projectId}`;
  const scoped = runtimeScopedSidebarKey(environmentId, projectId, environments);
  return scoped === raw ? [raw] : [raw, scoped];
}

/** The one lookup both layouts use, so neither spells out the key or the alias. */
export function lookupProjectDisplay(
  displayByKey: ReadonlyMap<string, ProjectDisplayEntry>,
  anchor: { readonly environmentId: string; readonly projectId: string },
  environments: SavedEnvironmentRuntimeIndex = {},
): ProjectDisplayEntry | undefined {
  const entries = projectDisplayKeys(anchor.environmentId, anchor.projectId, environments).flatMap(
    (key) => displayByKey.get(key) ?? [],
  );
  const [first] = entries;
  if (!first) return undefined;
  // The raw key and the alias are filled from different places: a connected
  // runtime registers the raw `env_…` key with the live name and cwd, while the
  // CMMD icon is overlaid onto the group's own keys. Returning the first hit
  // alone handed the live row a favicon path and no icon, which is the letter
  // avatar seen on forge-cloud after the first send (FOR-36687).
  const canonicalIdentity =
    first.canonicalIdentity ?? entries.find((entry) => entry.canonicalIdentity)?.canonicalIdentity;
  return canonicalIdentity && !first.canonicalIdentity ? { ...first, canonicalIdentity } : first;
}

/**
 * Take each group's CMMD icon onto every project key it stands for.
 *
 * Only the icon. A CMMD-backed project owns it, and nothing else in this index
 * can supply it — a cloud project has no cwd for the favicon path to read, so
 * its cards fell back to a letter avatar while the picker, which resolves from
 * the group, drew the real one.
 *
 * The name is deliberately NOT taken: `displayName` is the sidebar's derived
 * row label (a worktree/path label), while this index carries the project's own
 * name. Overlaying it renamed local rows from "Repo A" to "server".
 */
function overlayCanonicalProjectIcons(
  displayByKey: Map<string, ProjectDisplayEntry>,
  groups: readonly ProjectGroup[],
  environments: SavedEnvironmentRuntimeIndex,
): void {
  for (const group of groups) {
    const canonicalIdentity = resolveCanonicalProjectIdentity(group);
    if (!canonicalIdentity) continue;
    const keys = [
      ...projectDisplayKeys(
        group.representative.environmentId,
        group.representative.id,
        environments,
      ),
      ...group.memberProjectRefs.flatMap((ref) =>
        projectDisplayKeys(ref.environmentId, ref.projectId, environments),
      ),
    ];
    for (const key of keys) {
      const existing = displayByKey.get(key);
      displayByKey.set(key, {
        name: existing?.name ?? null,
        cwd: existing?.cwd ?? null,
        canonicalIdentity,
      });
    }
  }
}
